import { Icon } from "@chakra-ui/react";
import { EmrActions } from "@medstonetech/slate-icons";
import { usePermissions } from "contexts";
import { TriageChartCode } from "enums";
import {
  ChartRow,
  ChartRowElement,
  ChartRowGroup,
  ChartSection,
  ChartSectionHeader,
  ChartSubsection,
  ChartSubsectionContent,
  ChartSubsectionHeader,
} from "modules";
import { FormProvider } from "react-hook-form";
import { Loading, StaffAvatar } from "shared";
import { useTriageChartActionsForm } from "../../hooks";
import { ActionsHeader } from "../../shared";
import { actionsConstants } from "../../shared/actions";

const sectionId = "SACT";
const CHART_CODE: TriageChartCode = "T001x";

function N01Actions() {
  const {
    getIsLoading,
    onSubmit,
    updateIsLoading,
    encounterId,
    useFormReturn,
    onSaveAndNext,
  } = useTriageChartActionsForm({ chartCode: CHART_CODE });
  const { scope } = usePermissions();
  const isReadOnly = !scope("nurse:actions").isEditable;

  const { getValues } = useFormReturn;
  const initialActionsSections = getValues("initialActionsSections");
  const actionsSections = getValues("actionsSections");

  if (getIsLoading) {
    return <Loading />;
  }

  return (
    <FormProvider {...useFormReturn}>
      <ChartSection
        onSubmit={onSubmit}
        disabled={isReadOnly}
        onRouteChangeSave={(onComplete) => {
          onSubmit();
          onComplete();
        }}
      >
        <ChartSectionHeader
          icon={<Icon as={EmrActions} />}
          showNotesBtn={false}
          showActionsBtns
          isLoading={updateIsLoading}
          onSaveAndNext={onSaveAndNext}
        >
          Actions {isReadOnly ? "(Read Only)" : ""}
        </ChartSectionHeader>
        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn={false}
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q001"
              subsectionLabel="Initial Actions"
            >
              Initial Actions
            </ChartSubsectionHeader>
          }
          content={
            <>
              <ActionsHeader />
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name={`initialActionsSections[0].${CHART_CODE}${sectionId}Q001A000`}
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="ID"
                      name={`initialActionsSections[0].${CHART_CODE}${sectionId}Q001A001`}
                    />
                    <ChartRowElement
                      label="*"
                      type="label"
                      pl="unset"
                      ml="8px"
                      marginInline="unset"
                      color="red"
                      fontWeight="600"
                      justifyContent="flex-start"
                    />
                    <ChartRowElement
                      pl="20px"
                      type="option"
                      label="Allergy band"
                      name="initialActionsSections[0].T001xSACTQ001A002"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="applied"
                      name={`initialActionsSections[0].${CHART_CODE}${sectionId}Q001A003`}
                    />
                    <ChartRowElement
                      label="*"
                      type="label"
                      pl="unset"
                      ml="8px"
                      marginInline="unset"
                      color="red"
                      fontWeight="600"
                      justifyContent="flex-start"
                    />
                    <ChartRowElement
                      type="option"
                      label="verified"
                      name="initialActionsSections[0].T001xSACTQ001A004"
                    />
                    <ChartRowElement
                      label="*"
                      type="label"
                      pl="unset"
                      ml="8px"
                      marginInline="unset"
                      color="red"
                      fontWeight="600"
                      justifyContent="flex-start"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[0] &&
                          initialActionsSections[0].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[0] &&
                          initialActionsSections[0].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[1].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="c-collar"
                      name="initialActionsSections[1].T001xSACTQ001A005"
                    />
                    <ChartRowElement
                      type="option"
                      label="backboard"
                      name="initialActionsSections[1].T001xSACTQ001A006"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="applied"
                      name="initialActionsSections[1].T001xSACTQ001A007"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[1] &&
                          initialActionsSections[1].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[1] &&
                          initialActionsSections[1].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>

              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[2].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="disrobed"
                      name="initialActionsSections[2].T001xSACTQ001A008"
                    />
                    <ChartRowElement
                      type="option"
                      label="gowned"
                      name="initialActionsSections[2].T001xSACTQ001A009"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="blanked provided"
                      name="initialActionsSections[2].T001xSACTQ001A010"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[2] &&
                          initialActionsSections[2].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[2] &&
                          initialActionsSections[2].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>

              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[3].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="ice pack"
                      name="initialActionsSections[3].T001xSACTQ001A011"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="bandage"
                      name="initialActionsSections[3].T001xSACTQ001A012"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[3] &&
                          initialActionsSections[3].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[3] &&
                          initialActionsSections[3].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>

              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[4].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="bed low position"
                      name="initialActionsSections[4].T001xSACTQ001A013"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="side rails up"
                      name="initialActionsSections[4].T001xSACTQ001A014"
                    />
                    <ChartRowElement
                      type="option"
                      label="x 1"
                      name="initialActionsSections[4].T001xSACTQ001A015"
                    />
                    <ChartRowElement
                      type="option"
                      label="x 2"
                      name="initialActionsSections[4].T001xSACTQ001A016"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[4] &&
                          initialActionsSections[4].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[4] &&
                          initialActionsSections[4].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>

              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="initialActionsSections[5].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="call light in reach"
                      name="initialActionsSections[5].T001xSACTQ001A017"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="head of bed elevated"
                      name="initialActionsSections[5].T001xSACTQ001A018"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[5] &&
                          initialActionsSections[5].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[5] &&
                          initialActionsSections[5].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>

              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS
                  }
                >
                  <ChartRowGroup>
                    <ChartRowElement
                      type="date"
                      name="initialActionsSections[6].date"
                      size="lg"
                      marginRight="1.875rem"
                    />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="narcotic speech given"
                      name="initialActionsSections[6].T001xSACTQ001A019"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="medication speech given"
                      name="initialActionsSections[6].T001xSACTQ001A020"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (initialActionsSections[6] &&
                          initialActionsSections[6].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (initialActionsSections[6] &&
                          initialActionsSections[6].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
            </>
          }
        />

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn={false}
              chartId={CHART_CODE}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q002"
              subsectionLabel="Actions"
              marginLeft="1rem"
            >
              Actions
            </ChartSubsectionHeader>
          }
          content={
            <>
              <ActionsHeader />
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="actionsSections[0].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      name="actionsSections[0].T001xSACTQ002A031"
                      label="visual acuity:"
                    />
                    <ChartRowElement
                      type="circle-option"
                      label="L"
                      name="actionsSections[0].T001xSACTQ002A001"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[0].T001xSACTQ002A032"
                      label="20/"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[0].T001xSACTQ002A002"
                      size="md"
                    />
                    <ChartRowElement
                      type="circle-option"
                      label="R"
                      name="actionsSections[0].T001xSACTQ002A003"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[0].T001xSACTQ002A033"
                      label="20/"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[0].T001xSACTQ002A004"
                      size="md"
                    />
                    <ChartRowElement
                      type="option"
                      label="both"
                      name="actionsSections[0].T001xSACTQ002A005"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[0].T001xSACTQ002A034"
                      label="20/"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[0].T001xSACTQ002A006"
                      size="md"
                    />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[0] &&
                          actionsSections[0].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[0] &&
                          actionsSections[0].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowGroup width="200px">
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="corrected"
                      name="actionsSections[0].T001xSACTQ002A007"
                      marginLeft="3rem"
                    />
                    <ChartRowElement
                      type="option"
                      label="uncorrected"
                      name="actionsSections[0].T001xSACTQ002A008"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="actionsSections[1].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="set up"
                      name="actionsSections[1].T001xSACTQ002A009"
                    />
                    <ChartRowElement
                      type="option"
                      label="suture tray"
                      name="actionsSections[1].T001xSACTQ002A010"
                    />
                    <ChartRowElement
                      type="option"
                      label="eye tray"
                      name="actionsSections[1].T001xSACTQ002A011"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[1] &&
                          actionsSections[1].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[1] &&
                          actionsSections[1].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="actionsSections[2].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="cardiac monitor"
                      name="actionsSections[2].T001xSACTQ002A012"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[2] &&
                          actionsSections[2].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[2] &&
                          actionsSections[2].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="actionsSections[3].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="pulse oximeter"
                      name="actionsSections[3].T001xSACTQ002A013"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[3].T001xSACTQ002A014"
                      size="md"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[3].T001xSACTQ002A035"
                      label="%"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[3].T001xSACTQ002A036"
                      label="RA /"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[3].T001xSACTQ002A015"
                      size="md"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[3].T001xSACTQ002A037"
                      label="L"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[3] &&
                          actionsSections[3].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[3] &&
                          actionsSections[3].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="actionsSections[4].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="O2"
                      name="actionsSections[4].T001xSACTQ002A016"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[4].T001xSACTQ002A017"
                      size="md"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[4].T001xSACTQ002A038"
                      label="L"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[4].T001xSACTQ002A039"
                      label="via"
                    />
                    <ChartRowElement
                      type="option"
                      label="NC"
                      name="actionsSections[4].T001xSACTQ002A018"
                    />
                    <ChartRowElement
                      type="option"
                      label="mask"
                      name="actionsSections[4].T001xSACTQ002A019"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[4] &&
                          actionsSections[4].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[4] &&
                          actionsSections[4].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="actionsSections[5].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="bedside glucose"
                      name="actionsSections[5].T001xSACTQ002A020"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[5].T001xSACTQ002A021"
                      size="md"
                    />
                    <ChartRowElement
                      type="option"
                      name="actionsSections[5].T001xSACTQ002A040"
                      label="mg/dL"
                    />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[5] &&
                          actionsSections[5].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[5] &&
                          actionsSections[5].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="actionsSections[6].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="ready for provider eval"
                      name="actionsSections[6].T001xSACTQ002A022"
                    />
                    <ChartRowElement
                      type="option"
                      label="notified provider"
                      name="actionsSections[6].T001xSACTQ002A023"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[6] &&
                          actionsSections[6].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[6] &&
                          actionsSections[6].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="actionsSections[7].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="c-collar"
                      name="actionsSections[7].T001xSACTQ002A024"
                    />
                    <ChartRowElement
                      type="option"
                      label="backboard"
                      name="actionsSections[7].T001xSACTQ002A025"
                    />
                    <ChartRowElement
                      type="option"
                      label="removed"
                      name="actionsSections[7].T001xSACTQ002A026"
                    />
                    <ChartRowElement
                      type="option"
                      label="cleared by"
                      name="actionsSections[7].T001xSACTQ002A027"
                    />
                    <ChartRowElement
                      type="input"
                      name="actionsSections[7].T001xSACTQ002A028"
                    />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[7] &&
                          actionsSections[7].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[7] &&
                          actionsSections[7].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
              <ChartSubsectionContent
                padding={actionsConstants.CONTAINER_PADDING}
              >
                <ChartRow
                  templateColumns={
                    actionsConstants.ACTIONS_ROW_TEMPLATE_COLUMNS_2
                  }
                >
                  <ChartRowElement
                    type="date"
                    name="actionsSections[8].date"
                    size="lg"
                    marginRight="1.875rem"
                  />
                  <ChartRowGroup>
                    <ChartRowElement
                      type="option"
                      label="restraints"
                      name="actionsSections[8].T001xSACTQ002A029"
                    />
                    <ChartRowElement
                      type="option"
                      label="see documentation"
                      name="actionsSections[8].T001xSACTQ002A030"
                    />
                    <ChartRowElement type="empty" />
                  </ChartRowGroup>
                  <ChartRowGroup>
                    <StaffAvatar
                      size="xs"
                      w="32px"
                      h="32px"
                      fontSize=".25rem"
                      userName={
                        (actionsSections[8] &&
                          actionsSections[8].createdByFullName) ||
                        ""
                      }
                      profileUrl={
                        (actionsSections[8] &&
                          actionsSections[8].createdByPictureUrl) ||
                        ""
                      }
                      justifySelf="center"
                    />
                  </ChartRowGroup>
                </ChartRow>
              </ChartSubsectionContent>
            </>
          }
        />
      </ChartSection>
    </FormProvider>
  );
}

export { N01Actions };
